import React from 'react';
import AnswerCard from './AnswerCard.jsx';
import OverviewCards from './OverviewCards.jsx';
import ClarifyCard from './ClarifyCard.jsx';
import UnsupportedCard from './UnsupportedCard.jsx';
import ReportSuggestedCard from './ReportSuggestedCard.jsx';
import TableCard from './TableCard.jsx';
import ComparisonCard from './ComparisonCard.jsx';
import './analyticsAsk.css';

// Pure module: NO context/service/i18n imports, so it is statically testable in this
// no-jsdom workspace (importing the container would pull in i18n, which touches `document`).

/** Classify a thrown ask error into a panel status. Only the explicit FEATURE_DISABLED
 *  contract is a calm disabled state; a bare 401/403 (auth/RBAC) falls through to the
 *  generic error path so a real regression is not masked (Codex code-review P2). */
export function classifyAskError(err) {
  if (err?.body?.code === 'FEATURE_DISABLED') return 'disabled';
  return 'error';
}

/** The generic "something went wrong" block — shared by a transport-level error AND a
 *  status==='done' result whose `kind` is not one this component recognizes (codex P2.1:
 *  unrecognized `kind` is contract drift, not a reason to render a blank panel). */
function ErrorBlock({ onRetry }) {
  return (
    <div className="aa-error">
      <p>Sorry — I couldn’t get that just now. Please try again.</p>
      <button type="button" onClick={onRetry}>Retry</button>
    </div>
  );
}

/** Pure presentational result body — maps (status, result) → the right card/message.
 *  `onClarifyPick`/`onNearestTap`/`onSuggestionPick`/`suggestions`/`onTableFollowUp`/
 *  `onTablePresetChange` are threaded through from the container (AnalyticsAsk.jsx owns the
 *  actual /metric calls) — this module stays pure. `onTableFollowUp` now receives the WHOLE
 *  follow_up chip (`{label, body}`, #1692) and `onTablePresetChange` is a SEPARATE callback for
 *  preset chips (#1694 Task 9) — a preset is a card-local definition toggle, not a new question,
 *  so it must never be routed through the follow-up path. The backend no longer emits
 *  `cannot_answer` (replaced by the graceful-degrade `unsupported` kind, #1658) — there is
 *  deliberately no branch for it here. */
export function AnalyticsAskResult({
  status, result, onGenerateReport, onRetry, onClarifyPick, onNearestTap, onSuggestionPick, suggestions,
  onTableFollowUp, onTablePresetChange,
}) {
  if (status === 'loading') return <p className="aa-loading">Working it out…</p>;
  if (status === 'disabled') return <p className="aa-loading">Analytics is not enabled for your organisation.</p>;
  if (status === 'error') return <ErrorBlock onRetry={onRetry} />;
  if (status === 'idle' || !result) return null;
  switch (result.kind) {
    case 'answer':
      return <AnswerCard presentation={result.presentation} />;
    case 'table':
      return <TableCard presentation={result.presentation} onFollowUp={onTableFollowUp} onPresetChange={onTablePresetChange} />;
    case 'comparison':
      return <ComparisonCard comparison={result} />;
    case 'overview':
      return <OverviewCards cards={result.cards} scopeApplied={result.scope_applied} />;
    case 'clarify':
      return <ClarifyCard question={result.question} options={result.options} note={result.note} onPick={onClarifyPick} />;
    case 'unsupported':
      return (
        <UnsupportedCard
          message={result.message}
          nearest={result.nearest}
          onRunNearest={onNearestTap}
          onPickSuggestion={onSuggestionPick}
          suggestions={suggestions}
        />
      );
    case 'report_suggested':
      return <ReportSuggestedCard reason={result.reason} onGenerateReport={onGenerateReport} />;
    default:
      // Unrecognized kind (contract drift) — surface it as a retryable error, not a blank panel.
      return <ErrorBlock onRetry={onRetry} />;
  }
}
